import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Rocket, DollarSign, Users, TrendingUp, CheckCircle2, Copy } from "lucide-react";

export default function AffiliateSignup() {
  const { user, login, checkAuth } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", payout_method: "bkash", payout_account: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("নাম আর ইমেইল দিন");
    setBusy(true);
    try {
      const r = await api.post("/affiliate/signup", form);
      if (r.data.session_token) localStorage.setItem("oxy_token", r.data.session_token);
      setResult(r.data);
      await checkAuth();
      toast.success(r.data.already_registered ? "আপনি আগেই রেজিস্টার্ড" : "স্বাগতম! আপনার এফিলিয়েট কোড তৈরি");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const link = result ? `${window.location.origin}/?ref=${result.code}` : "";
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Copied!"); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-rose-50">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">← Back</Link>
        <div className="grid md:grid-cols-2 gap-10 mt-6">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-amber-200 text-amber-800 text-xs font-bold uppercase">Earn with OxyCom</div>
            <h1 className="mt-3 font-display text-4xl font-black">Become an affiliate <span className="text-amber-600">today.</span></h1>
            <p className="font-bn mt-3 text-slate-600 text-lg">
              প্রতিটা সফল রেফারেলে পাবেন কমিশন। সাইনআপ করেই পাবেন ইউনিক লিংক ও সব প্রোডাক্টের এফিলিয়েট URL।
            </p>
            <div className="mt-8 space-y-3">
              {[{icon:DollarSign,t:"High commission",d:"প্রতি বিক্রিতে ২০%"},{icon:Users,t:"Product-level links",d:"প্রত্যেক প্রোডাক্টের আলাদা লিংক"},{icon:TrendingUp,t:"Real-time tracking",d:"ক্লিক, সাইনআপ, কমিশন সব দেখা যায়"}].map((f,i)=>{
                const I=f.icon;return(
                <div key={i} className="flex gap-3 items-start"><div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0"><I className="w-5 h-5 text-amber-700"/></div><div><div className="font-bold text-slate-900">{f.t}</div><div className="font-bn text-sm text-slate-500">{f.d}</div></div></div>
              );})}
            </div>
          </div>

          {result ? (
            <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 shadow-xl">
              <CheckCircle2 className="w-12 h-12 text-emerald-500"/>
              <h2 className="mt-4 font-display text-2xl font-bold">You're in!</h2>
              <p className="font-bn text-slate-600 mt-1">আপনার এফিলিয়েট কোড:</p>
              <div className="mt-3 p-4 rounded-xl bg-slate-900 text-amber-400 font-mono text-lg font-bold text-center">{result.code}</div>
              <div className="mt-4">
                <Label className="text-xs">Your referral link</Label>
                <div className="mt-1 flex gap-2">
                  <Input readOnly value={link} className="font-mono text-xs"/>
                  <Button onClick={copy} variant="outline" size="icon"><Copy className="w-4 h-4"/></Button>
                </div>
              </div>
              <Button onClick={() => nav("/affiliate/products")} className="mt-6 w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full font-bold" data-testid="aff-view-products">
                See all product links <Rocket className="w-4 h-4 ml-2"/>
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-xl">
              <h2 className="font-display text-2xl font-bold">Sign up</h2>
              <div className="space-y-3 mt-5">
                <div><Label>Name / নাম</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="aff-name"/></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="aff-email"/></div>
                <div><Label>Phone / ফোন</Label><Input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="aff-phone"/></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Payout</Label>
                    <select value={form.payout_method} onChange={e=>setForm({...form,payout_method:e.target.value})} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                      <option value="bkash">bKash</option><option value="nagad">Nagad</option><option value="bank">Bank</option>
                    </select>
                  </div>
                  <div><Label>Account</Label><Input value={form.payout_account} onChange={e=>setForm({...form,payout_account:e.target.value})}/></div>
                </div>
              </div>
              <Button onClick={submit} disabled={busy} className="w-full mt-6 h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-full" data-testid="aff-submit">
                {busy ? "Signing up…" : "Create affiliate account"}
              </Button>
              <p className="text-xs text-center text-slate-500 mt-4">
                অলরেডি এফিলিয়েট? <button onClick={login} className="text-amber-600 font-semibold underline">Sign in with Google</button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
